import { useEffect, useState } from "react";
import { MessageCircle, Phone, ArrowRight } from "lucide-react";
import { ANDREA_PHONE, ANDREA_PHONE_DISPLAY, CALENDLY_URL, contactClick } from "../lib/contact";
import { trackEvent } from "../lib/tracking";

// Persistent conversion concierge (evolution of the original Text Andrea FAB).
// Hierarchy: Book my complimentary sleep assessment (dominant) > Text Andrea / Call Andrea (quiet).
// Appears only after the hero has been scrolled past — the opening breathes first.
// Retreats for the footer, form focus / mobile keyboard, and the guided
// conversation (hidden prop). Mobile: bottom bar. Desktop: compact corner cluster.
// All three actions are plain sms:/tel:/Calendly links — no widgets, no chat UI.
export const TextAndrea = ({ hidden = false }) => {
  const [pastHero, setPastHero] = useState(false);
  const [footerInView, setFooterInView] = useState(false);
  const [typing, setTyping] = useState(false);
  const [coveringHowItWorks, setCoveringHowItWorks] = useState(false);

  useEffect(() => {
    const hero = document.querySelector('[data-testid="hero-section"]');
    const footer = document.querySelector('[data-testid="site-footer"]');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.target === hero) setPastHero(!e.isIntersecting && e.boundingClientRect.top < 0);
          if (e.target === footer) setFooterInView(e.isIntersecting);
        });
      },
      { threshold: 0 },
    );
    if (hero) io.observe(hero);
    if (footer) io.observe(footer);

    // Sections that carry their own identical Book CTA get the moment to
    // themselves: whenever the bar's fixed position would geometrically
    // overlap one of them, the bar fades out and hands conversion to the
    // section. Checked on scroll/resize (rAF-throttled).
    const yieldSections = ["how-it-works-section", "free-assessment-section"]
      .map((t) => document.querySelector(`[data-testid="${t}"]`))
      .filter(Boolean);
    let raf = 0;
    const checkOverlap = () => {
      raf = 0;
      const bar = document.querySelector(
        '[data-testid="persistent-actions"], [data-testid="persistent-actions-desktop"]'
      );
      if (!yieldSections.length || !bar) return;
      const b = bar.getBoundingClientRect();
      setCoveringHowItWorks(
        yieldSections.some((el) => {
          const a = el.getBoundingClientRect();
          return !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom);
        })
      );
    };
    const onScrollResize = () => { if (!raf) raf = requestAnimationFrame(checkOverlap); };
    window.addEventListener("scroll", onScrollResize, { passive: true });
    window.addEventListener("resize", onScrollResize);
    checkOverlap();

    const isField = (el) => Boolean(el?.closest?.("input, textarea, select"));
    const onFocusIn = (e) => { if (isField(e.target)) setTyping(true); };
    const onFocusOut = () =>
      setTimeout(() => { if (!isField(document.activeElement)) setTyping(false); }, 60);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);

    const vv = window.visualViewport;
    const onViewport = () => { if (vv) setTyping(vv.height < window.innerHeight * 0.75); };
    vv?.addEventListener("resize", onViewport);

    return () => {
      io.disconnect();
      window.removeEventListener("scroll", onScrollResize);
      window.removeEventListener("resize", onScrollResize);
      if (raf) cancelAnimationFrame(raf);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      vv?.removeEventListener("resize", onViewport);
    };
  }, []);

  if (!ANDREA_PHONE) return null;

  const visible = !hidden && pastHero && !footerInView && !typing && !coveringHowItWorks;
  const stateCls = visible
    ? "opacity-100 translate-y-0"
    : "opacity-0 translate-y-5 invisible pointer-events-none";
  const motionCls =
    "transition-[opacity,transform,visibility] duration-500 ease-out motion-reduce:transition-none motion-reduce:transform-none";

  // CTA-interaction events only — a click is never a completed call or
  // conversation. contactClick adds the desktop number-and-copy fallback;
  // on touch devices it returns immediately and native sms:/tel: runs.
  const track = (event, location) => (e) => {
    trackEvent(event, { location });
    if (event === "text_andrea_click") contactClick("text")(e);
    else if (event === "call_andrea_click") contactClick("call")(e);
  };

  const quietLink =
    "inline-flex items-center justify-center gap-2 py-3.5 text-[11px] tracking-[0.16em] uppercase font-semibold text-smoke hover:text-ink focus-visible:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay/60 rounded-lg transition-colors duration-300";

  const bookCls =
    "inline-flex items-center justify-center gap-2 rounded-full bg-ink text-ivory text-sm font-medium hover:bg-espresso focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay focus-visible:ring-offset-2 focus-visible:ring-offset-ivory transition-colors duration-300";

  return (
    <>
      {/* Mobile: sticky bottom conversion bar (< lg) */}
      <nav
        aria-label="Quick contact and booking"
        data-testid="persistent-actions"
        className={`fixed inset-x-0 bottom-0 z-[60] px-4 sm:px-6 pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:hidden ${motionCls} ${stateCls}`}
      >
        <div className="mx-auto max-w-md rounded-[1.6rem] border border-ink/10 bg-ivory/95 backdrop-blur-md shadow-[0_20px_50px_-18px_rgba(36,28,19,0.45)] px-3 pt-1.5 pb-3">
          <div className="flex items-stretch divide-x divide-ink/10 border-b border-ink/5">
            <a
              data-testid="persistent-text-cta"
              href={`sms:${ANDREA_PHONE}`}
              aria-label={`Text Andrea at ${ANDREA_PHONE_DISPLAY} — opens your messaging app`}
              onClick={track("text_andrea_click", "persistent_bar")}
              className={`flex-1 ${quietLink}`}
            >
              <MessageCircle size={14} aria-hidden="true" />
              Text Andrea
            </a>
            <a
              data-testid="persistent-call-cta"
              href={`tel:${ANDREA_PHONE}`}
              aria-label={`Call Andrea at ${ANDREA_PHONE_DISPLAY}`}
              onClick={track("call_andrea_click", "persistent_bar")}
              className={`flex-1 ${quietLink}`}
            >
              <Phone size={14} aria-hidden="true" />
              Call Andrea
            </a>
          </div>
          <a
            data-testid="persistent-book-cta"
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Book my complimentary sleep assessment — opens Andrea's calendar in a new tab"
            onClick={track("book_assessment_click", "persistent_bar")}
            className={`mt-2.5 w-full py-4 ${bookCls}`}
          >
            Book my complimentary sleep assessment
            <ArrowRight size={15} aria-hidden="true" />
          </a>
        </div>
      </nav>

      {/* Desktop: compact corner concierge (lg+) — quiet text links above one dominant pill */}
      <nav
        aria-label="Quick contact and booking"
        data-testid="persistent-actions-desktop"
        className={`hidden lg:flex fixed bottom-10 right-10 z-[60] flex-col items-end gap-3 ${motionCls} ${stateCls}`}
      >
        <div className="flex items-center gap-4 rounded-full border border-ink/10 bg-ivory/85 backdrop-blur-md px-4 py-1">
          <a
            data-testid="persistent-text-cta-desktop"
            href={`sms:${ANDREA_PHONE}`}
            aria-label={`Text Andrea at ${ANDREA_PHONE_DISPLAY} — opens your messaging app`}
            onClick={track("text_andrea_click", "persistent_desktop")}
            className="inline-flex items-center gap-1.5 py-2 text-[11px] tracking-[0.16em] uppercase font-semibold text-smoke hover:text-ink focus-visible:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay/60 rounded underline-offset-4 hover:underline decoration-ink/20 transition-colors duration-300"
          >
            <MessageCircle size={13} aria-hidden="true" />
            Text Andrea
          </a>
          <span aria-hidden="true" className="h-3 w-px bg-ink/15" />
          <a
            data-testid="persistent-call-cta-desktop"
            href={`tel:${ANDREA_PHONE}`}
            aria-label={`Call Andrea at ${ANDREA_PHONE_DISPLAY}`}
            onClick={track("call_andrea_click", "persistent_desktop")}
            className="inline-flex items-center gap-1.5 py-2 text-[11px] tracking-[0.16em] uppercase font-semibold text-smoke hover:text-ink focus-visible:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay/60 rounded underline-offset-4 hover:underline decoration-ink/20 transition-colors duration-300"
          >
            <Phone size={13} aria-hidden="true" />
            Call Andrea
          </a>
        </div>
        <a
          data-testid="persistent-book-cta-desktop"
          href={CALENDLY_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Book my complimentary sleep assessment — opens Andrea's calendar in a new tab"
          onClick={track("book_assessment_click", "persistent_desktop")}
          className={`pl-6 pr-7 py-4 shadow-[0_16px_40px_-12px_rgba(36,28,19,0.45)] ring-1 ring-ivory/35 ${bookCls}`}
        >
          Book my complimentary sleep assessment
          <ArrowRight size={15} aria-hidden="true" />
        </a>
      </nav>
    </>
  );
};
